import React, { useState } from 'react';
import { doc, setDoc } from 'firebase/firestore';
import { signInAnonymously } from 'firebase/auth';
import db, { auth } from '../firebase';
import config from '../config';
import RatingIcon from './RatingIcon';
import { showToast, launchConfetti } from '../utils/feedback';
import { searchUrl } from '../utils/links';

const RATING_WORDS = [
  'Tap a bottle to rate',
  'Watery 💧',
  "Meh, it's fine 😐",
  'Solid ranch 🥄',
  'Really good 😋',
  'Elite ranch! 🌟',
];

const fieldStyle = {
  width: '100%',
  border: '1px solid #48484a',
  padding: '11px 14px',
  borderRadius: 12,
  fontSize: 15,
  background: '#2c2c2e',
  boxSizing: 'border-box',
  color: '#f5f5f7',
};

const labelStyle = {
  display: 'block',
  fontSize: 13,
  fontWeight: 600,
  color: '#f5f5f7',
  marginBottom: 7,
  letterSpacing: '-0.01em',
};

function RatingForm({ restaurant, setRestaurant, restaurants = [], onSubmitted }) {
  const [condiment, setCondiment] = useState('Ranch');
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const active = hover || rating;
  const ratingWord = RATING_WORDS[active] || RATING_WORDS[0];
  const ratingWordColor = active === 0 ? '#8e8e93' : '#30d158';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return; // guard against double-submit

    // Normalize the typed name: trim + collapse inner whitespace, then reuse
    // the canonical casing of a known restaurant if one matches.
    const cleaned = restaurant.trim().replace(/\s+/g, ' ');
    const name =
      restaurants.find((r) => r.toLowerCase() === cleaned.toLowerCase()) || cleaned;

    if (!name) {
      showToast('Pick or type a restaurant first! 🍗');
      return;
    }
    if (!rating) {
      showToast('Pick a rating first! 🥄');
      return;
    }

    setSubmitting(true);
    try {
      // Ensure we have an anonymous identity, then write to a document id
      // pinned to (user, condiment, restaurant). This is what limits each
      // person to one vote per restaurant/condiment — re-rating overwrites
      // their own slot instead of stuffing the ballot.
      let user = auth.currentUser;
      if (!user) {
        user = (await signInAnonymously(auth)).user;
      }
      const ratingId = `${user.uid}_${condiment}_${name.toLowerCase()}`;

      await setDoc(doc(db, 'ratings', ratingId), {
        restaurant: name,
        condiment,
        rating,
        comment: comment.trim(),
        createdAt: new Date(),
        uid: user.uid,
      });

      if (config.confetti) {
        launchConfetti();
        showToast('Rating submitted! 🌟');
      } else {
        window.alert('Rating submitted! 🌟');
      }

      // Reset the form
      setRestaurant('');
      setCondiment('Ranch');
      setRating(0);
      setHover(0);
      setComment('');

      if (onSubmitted) onSubmitted();
    } catch (error) {
      console.error('Error adding rating:', error);
      if (error.code && error.code.startsWith('auth/')) {
        // Anonymous sign-in not enabled/configured in the Firebase console.
        showToast('Ratings are temporarily unavailable.');
      } else {
        showToast('Oops! Something went wrong.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      style={{
        margin: 0,
        padding: '8px 0 0',
        background: 'transparent',
        display: 'flex',
        flexDirection: 'column',
        gap: 22,
        boxSizing: 'border-box',
      }}
    >
      <h2
        style={{
          margin: 0,
          fontSize: 21,
          lineHeight: '28px',
          fontWeight: 600,
          letterSpacing: '-0.01em',
          textAlign: 'center',
          color: '#f5f5f7',
        }}
      >
        Leave a rating
      </h2>

      {/* Restaurant + Search link */}
      <div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 10,
            marginBottom: 7,
          }}
        >
          <label style={{ ...labelStyle, marginBottom: 0 }}>Select Restaurant</label>
          <a
            href={searchUrl(restaurant)}
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontSize: 13, fontWeight: 600, color: '#0a84ff', textDecoration: 'none' }}
          >
            Search 🔍
          </a>
        </div>
        <input
          className="rtr-field"
          type="text"
          list="restaurant-options"
          value={restaurant}
          onChange={(e) => setRestaurant(e.target.value)}
          placeholder="Type or choose a restaurant..."
          autoComplete="off"
          disabled={submitting}
          style={fieldStyle}
        />
        <datalist id="restaurant-options">
          {restaurants.map((name) => (
            <option key={name} value={name} />
          ))}
        </datalist>
      </div>

      {/* Condiment */}
      <div>
        <label style={labelStyle}>Condiment</label>
        <select
          className="rtr-field"
          value={condiment}
          onChange={(e) => setCondiment(e.target.value)}
          disabled={submitting}
          style={fieldStyle}
        >
          <option value="Ranch">Ranch</option>
          <option value="BBQ">BBQ</option>
          <option value="Honey Mustard">Honey Mustard</option>
          <option value="Blue Cheese">Blue Cheese</option>
        </select>
      </div>

      {/* Rating control */}
      <div>
        <label style={labelStyle}>Rating</label>
        <div
          className={`rtr-ratingrow ${config.bounceOnSelect ? 'rtr-bounce' : ''}`}
          style={{ display: 'flex', justifyContent: 'center', gap: 6 }}
        >
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              type="button"
              className="rtr-star-btn"
              onClick={() => setRating(star)}
              onMouseEnter={() => setHover(star)}
              onMouseLeave={() => setHover(0)}
              onFocus={() => setHover(star)}
              onBlur={() => setHover(0)}
              aria-label={`Rate ${star} out of 5`}
              aria-pressed={rating === star}
              disabled={submitting}
            >
              <RatingIcon on={active >= star} />
            </button>
          ))}
        </div>
        {config.ratingWord && (
          <div
            style={{
              textAlign: 'center',
              minHeight: 22,
              marginTop: 12,
              fontSize: 15,
              fontWeight: 700,
              color: ratingWordColor,
              transition: 'color 160ms ease',
            }}
          >
            {ratingWord}
          </div>
        )}
      </div>

      {/* Comment */}
      <div>
        <label style={labelStyle}>Comment</label>
        <textarea
          className="rtr-field"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          rows="3"
          placeholder="What did you think of the ranch?"
          disabled={submitting}
          style={{ ...fieldStyle, fontFamily: 'inherit', resize: 'vertical' }}
        />
      </div>

      <button
        type="submit"
        className="rtr-submit"
        disabled={submitting}
        aria-busy={submitting}
        style={{
          width: '100%',
          padding: '13px 16px',
          color: '#fff',
          background: '#22c55e',
          border: 'none',
          borderRadius: 12,
          fontSize: 16,
          fontWeight: 600,
          letterSpacing: '-0.01em',
          cursor: 'pointer',
          marginTop: 2,
        }}
      >
        {submitting ? 'Submitting…' : 'Submit Rating'}
      </button>
    </form>
  );
}

export default RatingForm;
